/**
 * CategoryMergeDialog — merge one category INTO another (PIM Wave P1 Task 7,
 * design notes Surface 3, brief §2). USER RULING C2 (binding): the backend
 * merge route is NOT a dry-run — there is no impact/preview endpoint, so
 * this dialog cannot show pre-merge counts. Flow instead: pick a target →
 * explicit irreversible-warning confirm (source + target names, no numbers)
 * → `POST .../merge` → result summary rendered from the returned counts.
 *
 * Three internal steps (`pick` / `confirm` / `result`), state kept local —
 * no precedent exists for a multi-step-in-one-sheet flow (recon: "keep step
 * state local, simple"). The whole component remounts per open (the caller
 * keys it on the source category id, `CategoryFormDialog` precedent), so
 * step/selection state resets for free between opens.
 *
 * STEP pick reuses `_shared/CategoryTree` READ-ONLY (no `onReorderSiblings`
 * passed → dnd fully disabled, no additive prop needed on the primitive —
 * it was already built dnd-optional). Rows behave like a radiogroup: click
 * selects, `Check` + token treatment mark the selected row. Excluded from
 * selectability — rendered dimmed, `disabled`, with a `title` explaining why
 * — are: the source category itself, every descendant of the source
 * (computed client-side from the flat list, since reparenting a target
 * inside the source's own subtree would create a `parent_id` cycle — the
 * exact 422 the backend raises), and archived categories (the backend
 * refuses to merge INTO an archived target). The SOURCE itself may already
 * be archived — verified in `service.py` (`merge_category` docstring line
 * 1302: "The SOURCE may already be archived") — so `CategoryListPage` does
 * NOT gate the "Merge into…" row action on `is_archived`.
 *
 * The search input + match/ancestor-expansion behavior mirrors
 * `CategoryListPage.tsx`'s search idiom, but is a LOCAL copy here: none of
 * `categoryLabel`/`compareSiblings`/`ancestorIdsOf`/the matching logic are
 * exported from that page (verified — no `export` keyword on any of them),
 * and the brief explicitly allows a local copy when nothing is exported.
 * Noted in the P1 Task 7 report per the brief's ask.
 *
 * STEP confirm/result map the four backend guards (self / target-descendant
 * / target-archived / child-slug-conflict — the last one is NOT
 * client-detectable, since it depends on both trees' direct children server
 * side) to inline copy; unknown codes fall back to a generic error. The
 * dialog stays open on error so the admin can go Back and re-pick.
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Check } from "lucide-react";

import { BottomSheet, Button } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { CategoryTree, type CategoryTreeRenderMeta } from "@/features/admin/_shared";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { ApiError } from "@/lib/api-client";
import { useMergeCategory } from "@/features/admin/categories/hooks";
import type { AdminCategory, CategoryMergeResponse } from "@/features/admin/categories/api";

/** `CategoryTree` is generic over `{id, parentId}` — same camelCase-alias
 * pattern as `CategoryListPage.tsx`'s `CategoryNode`. */
type MergeTreeNode = AdminCategory & { parentId: string | null };

type Step = "pick" | "confirm" | "result";

/** Mirrors the backend tree ORDER BY (`marketplace/repository.py:127`) —
 * same comparator as `CategoryListPage.tsx` (local copy, see file header). */
function compareSiblings(a: AdminCategory, b: AdminCategory): number {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return a.name_ru.localeCompare(b.name_ru, "ru");
}

/** Current-admin-language label with RU fallback (local copy, see header). */
function categoryLabel(node: AdminCategory, lang: string): string {
  if (lang === "ky" && node.name_ky) return node.name_ky;
  if (lang === "en" && node.name_en) return node.name_en;
  return node.name_ru;
}

/** Walks `parent_id` up to the root, returning ancestor ids (local copy). */
function ancestorIdsOf(id: string, byId: Map<string, AdminCategory>): string[] {
  const ids: string[] = [];
  let current = byId.get(id);
  while (current && current.parent_id) {
    ids.push(current.parent_id);
    current = byId.get(current.parent_id);
  }
  return ids;
}

/** Every id in `source`'s subtree (children, grandchildren, …), computed
 * client-side from `parent_id` — mirrors the backend's authoritative
 * `parent_id` walk (`repository.descendant_category_ids`), never the stored
 * `path_slugs` (service.py:163-164 comment on why). */
function descendantIdsOf(sourceId: string, all: AdminCategory[]): Set<string> {
  const childrenByParent = new Map<string, AdminCategory[]>();
  for (const c of all) {
    if (c.parent_id === null) continue;
    const list = childrenByParent.get(c.parent_id);
    if (list) list.push(c);
    else childrenByParent.set(c.parent_id, [c]);
  }
  const result = new Set<string>();
  const stack = [sourceId];
  while (stack.length > 0) {
    const current = stack.pop();
    if (current === undefined) continue;
    for (const child of childrenByParent.get(current) ?? []) {
      if (!result.has(child.id)) {
        result.add(child.id);
        stack.push(child.id);
      }
    }
  }
  return result;
}

type ExcludeReason = "self" | "descendant" | "archived" | null;

/** Backend error code -> i18n KEY (the Zod-error idiom, A3: message strings
 * ARE i18n keys, resolved at render). Unmapped codes fall back to
 * `error.generic` at the call site. */
const MERGE_ERROR_KEY: Record<string, string> = {
  "marketplace.category.merge_self": "categories.merge.errors.self",
  "marketplace.category.merge_target_descendant": "categories.merge.errors.descendant",
  "marketplace.category.merge_target_archived": "categories.merge.errors.targetArchived",
  "marketplace.category.merge_child_slug_conflict": "categories.merge.errors.childSlugConflict",
};

function mergeErrorKey(err: unknown): string {
  if (err instanceof ApiError && err.code) {
    const key = MERGE_ERROR_KEY[err.code];
    if (key) return key;
  }
  return "error.generic";
}

export interface CategoryMergeDialogProps {
  open: boolean;
  /** The category being merged AWAY (its products/subtree/aliases move to
   * the picked target; it ends archived). `null` only while closed. */
  source: AdminCategory | null;
  /** Full flat admin category list (drained — `useAllAdminCategories()` at
   * the call site) — the picker's tree is built from this. */
  categories: AdminCategory[];
  onClose: () => void;
}

const RESULT_COUNT_KEYS: {
  field: keyof Omit<CategoryMergeResponse, "source_archived">;
  labelKey: string;
}[] = [
  { field: "products_repointed", labelKey: "categories.merge.result.productsRepointed" },
  { field: "links_moved", labelKey: "categories.merge.result.linksMoved" },
  { field: "links_deduped", labelKey: "categories.merge.result.linksDeduped" },
  { field: "children_reparented", labelKey: "categories.merge.result.childrenReparented" },
  { field: "aliases_moved", labelKey: "categories.merge.result.aliasesMoved" },
  { field: "aliases_dropped", labelKey: "categories.merge.result.aliasesDropped" },
];

export function CategoryMergeDialog({
  open,
  source,
  categories,
  onClose,
}: CategoryMergeDialogProps): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);
  const lang = i18n.language;
  const desktop = useIsDesktop();

  const byId = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const excludedDescendantIds = useMemo(
    () => (source ? descendantIdsOf(source.id, categories) : new Set<string>()),
    [source, categories],
  );

  const excludeReasonOf = (node: AdminCategory): ExcludeReason => {
    if (!source) return null;
    if (node.id === source.id) return "self";
    if (excludedDescendantIds.has(node.id)) return "descendant";
    if (node.is_archived) return "archived";
    return null;
  };

  const [step, setStep] = useState<Step>("pick");
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CategoryMergeResponse | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // a11y: move focus to the new step's heading so screen readers announce
  // it (design notes Surface global rule "steps announced (heading per
  // step)") — each step renders its own `<h2 ref={headingRef} tabIndex={-1}>`.
  // Deferred a frame (like `BottomSheet`'s own open-autofocus effect) and
  // ordered AFTER it (child effects — BottomSheet's — run before this
  // parent effect, so its queued rAF always fires first): on initial open
  // this wins over BottomSheet's default "focus first focusable" behavior;
  // on a later step change (`open` unchanged) `BottomSheet`'s effect
  // doesn't re-fire at all, so this is the only one moving focus.
  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(() => headingRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [step, open]);

  // --- Picker: expansion + search (local copy of CategoryListPage's idiom,
  // see file header). Default = roots expanded. ---
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(categories.filter((c) => c.parent_id === null).map((c) => c.id)),
  );
  const toggleExpanded = (id: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const isSearching = q !== "";

  const matchIds = useMemo(() => {
    if (!isSearching) return null;
    const set = new Set<string>();
    for (const c of categories) {
      if (
        c.name_ru.toLowerCase().includes(q) ||
        (c.name_ky ?? "").toLowerCase().includes(q) ||
        (c.name_en ?? "").toLowerCase().includes(q) ||
        c.slug.toLowerCase().includes(q)
      ) {
        set.add(c.id);
      }
    }
    return set;
  }, [categories, q, isSearching]);

  const visibleCategories = useMemo(() => {
    if (!isSearching || !matchIds) return categories;
    const visible = new Set<string>();
    for (const id of matchIds) {
      visible.add(id);
      for (const ancestorId of ancestorIdsOf(id, byId)) visible.add(ancestorId);
    }
    return categories.filter((c) => visible.has(c.id));
  }, [categories, isSearching, matchIds, byId]);

  const effectiveExpandedIds = useMemo(() => {
    if (!isSearching || !matchIds) return expandedIds;
    const merged = new Set(expandedIds);
    for (const id of matchIds) {
      for (const ancestorId of ancestorIdsOf(id, byId)) merged.add(ancestorId);
    }
    return merged;
  }, [isSearching, matchIds, byId, expandedIds]);

  const treeNodes: MergeTreeNode[] = useMemo(
    () => visibleCategories.map((c) => ({ ...c, parentId: c.parent_id })),
    [visibleCategories],
  );

  const mergeMutation = useMergeCategory();

  const selectedTarget = selectedTargetId ? (byId.get(selectedTargetId) ?? null) : null;
  const sourceLabel = source ? categoryLabel(source, lang) : "";
  const targetLabel = selectedTarget ? categoryLabel(selectedTarget, lang) : "";

  const goToConfirm = (): void => {
    if (!selectedTargetId) return;
    setError(null);
    setStep("confirm");
  };
  const goBackToPick = (): void => {
    setError(null);
    setStep("pick");
  };

  const handleConfirm = (): void => {
    if (!source || !selectedTargetId) return;
    setError(null);
    mergeMutation.mutate(
      { sourceId: source.id, body: { target_category_id: selectedTargetId } },
      {
        onSuccess: (data) => {
          setResult(data);
          setStep("result");
          toast.success(t("categories.merge.success"));
        },
        onError: (err) => setError(t(mergeErrorKey(err))),
      },
    );
  };

  const handleClose = (): void => {
    onClose();
  };

  const renderPickRow = (node: MergeTreeNode, _meta: CategoryTreeRenderMeta): ReactNode => {
    const label = categoryLabel(node, lang);
    const reason = excludeReasonOf(node);
    const selectable = reason === null;
    const selected = selectable && selectedTargetId === node.id;
    const title = reason ? t(`categories.merge.pick.excludeReason.${reason}`) : undefined;

    return (
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        disabled={!selectable}
        title={title}
        data-testid={`category-merge-pick-${node.id}`}
        onClick={() => selectable && setSelectedTargetId(node.id)}
        className={[
          "flex min-h-[44px] w-full items-center justify-between gap-2 rounded-[var(--g-radius-sm)] px-2 py-1 text-left transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
          selectable ? "cursor-pointer hover:bg-[var(--g-elevated)]" : "cursor-not-allowed opacity-40",
          selected ? "bg-[var(--g-primary-soft)] text-[var(--g-primary-ink)]" : "",
        ].join(" ")}
      >
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-medium">{label}</span>
          <span className="truncate font-mono text-xs text-[var(--g-secondary)] opacity-80">{node.slug}</span>
        </span>
        {selected ? (
          <Check size={16} strokeWidth={2} aria-hidden className="shrink-0 text-[var(--g-primary-ink)]" />
        ) : null}
      </button>
    );
  };

  return (
    <BottomSheet open={open} onClose={handleClose} title={t("categories.merge.dialogTitle")} desktop={desktop}>
      {!source ? null : step === "pick" ? (
        <div className="flex flex-col gap-3" data-testid="category-merge-step-pick">
          <h2 ref={headingRef} tabIndex={-1} className="text-base font-semibold text-foreground focus:outline-none">
            {t("categories.merge.pick.heading")}
          </h2>
          <input
            type="search"
            data-testid="category-merge-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("categories.merge.pick.searchPlaceholder")}
            aria-label={t("common.search")}
            className="min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
          />
          <div
            role="radiogroup"
            aria-label={t("categories.merge.pick.ariaLabel")}
            className="max-h-[50vh] overflow-y-auto"
          >
            <CategoryTree<MergeTreeNode>
              nodes={treeNodes}
              expandedIds={effectiveExpandedIds}
              onToggle={toggleExpanded}
              renderRow={renderPickRow}
              compare={compareSiblings}
              getToggleLabel={(node, expanded) =>
                t(expanded ? "categories.tree.collapse" : "categories.tree.expand", {
                  name: categoryLabel(node, lang),
                })
              }
              getDragHandleLabel={() => ""}
              testid="category-merge-tree"
            />
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="primary"
              size="sm"
              data-testid="category-merge-continue"
              disabled={!selectedTargetId}
              onClick={goToConfirm}
              className="min-h-[44px]"
            >
              {t("categories.merge.pick.continue")}
            </Button>
          </div>
        </div>
      ) : step === "confirm" ? (
        <div className="flex flex-col gap-4" data-testid="category-merge-step-confirm">
          <h2 ref={headingRef} tabIndex={-1} className="text-base font-semibold text-foreground focus:outline-none">
            {t("categories.merge.confirm.heading")}
          </h2>
          <p className="text-base font-semibold text-foreground" data-testid="category-merge-pair">
            {t("categories.merge.confirm.pair", { source: sourceLabel, target: targetLabel })}
          </p>
          <p className="text-sm text-[var(--g-secondary)]">
            {t("categories.merge.confirm.warning", { source: sourceLabel, target: targetLabel })}
          </p>
          {error ? (
            <p role="alert" data-testid="category-merge-error" className="text-sm text-[var(--g-error)]">
              {error}
            </p>
          ) : null}
          <div className="flex justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={mergeMutation.isPending}
              onClick={goBackToPick}
              className="min-h-[44px]"
            >
              {t("categories.merge.back")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              data-testid="category-merge-confirm"
              loading={mergeMutation.isPending}
              onClick={handleConfirm}
              className="min-h-[44px]"
            >
              {t("categories.merge.confirm.action")}
            </Button>
          </div>
        </div>
      ) : result ? (
        <div className="flex flex-col gap-4" data-testid="category-merge-step-result">
          <h2 ref={headingRef} tabIndex={-1} className="text-base font-semibold text-foreground focus:outline-none">
            {t("categories.merge.result.heading")}
          </h2>
          <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-2 text-sm" data-testid="category-merge-result">
            {RESULT_COUNT_KEYS.map(({ field, labelKey }) => (
              <div className="contents" key={field}>
                <dt className="text-[var(--g-secondary)]">{t(labelKey)}</dt>
                <dd className="text-right font-semibold tabular-nums text-foreground">{result[field]}</dd>
              </div>
            ))}
          </dl>
          <p className="text-sm font-medium text-foreground" data-testid="category-merge-source-archived">
            {t("categories.merge.result.sourceArchived")}
          </p>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="primary"
              size="sm"
              data-testid="category-merge-done"
              onClick={handleClose}
              className="min-h-[44px]"
            >
              {t("categories.merge.result.done")}
            </Button>
          </div>
        </div>
      ) : null}
    </BottomSheet>
  );
}

export default CategoryMergeDialog;
